import ReactGA from "react-ga4";

/**
 * Custom Experiment Parameter Tracker for A/B Testing Funnels
 * Uses custom params to avoid conflicts with UTM parameters used for ads
 *
 * Custom Parameters:
 * - exp_name: Experiment name (e.g., "bundle_test", "checkout_v2")
 * - exp_variant: Variant identifier (e.g., "variant_a", "variant_b", "control")
 * - exp_id: Optional experiment ID for tracking
 *
 * Example URL:
 * /CheckoutPage/?exp_name=bundle_test&exp_variant=variant_a
 */

export interface ExperimentParameters {
  exp_name: string | null;
  exp_variant: string | null;
  exp_id: string | null;
}

class ExperimentTracker {
  private urlParams: URLSearchParams;
  private experimentParams: ExperimentParameters;

  constructor() {
    this.urlParams = new URLSearchParams(window.location.search);
    this.experimentParams = this.extractExperimentParams();
  }

  /**
   * Extract experiment parameters from URL
   */
  private extractExperimentParams(): ExperimentParameters {
    return {
      exp_name: this.urlParams.get("exp_name"),
      exp_variant: this.urlParams.get("exp_variant"),
      exp_id: this.urlParams.get("exp_id"),
    };
  }

  /**
   * Get all experiment parameters
   */
  getParams(): ExperimentParameters {
    return this.experimentParams;
  }

  /**
   * Check if user is in an experiment
   */
  isInExperiment(): boolean {
    return this.experimentParams.exp_name !== null;
  }

  /**
   * Get current variant
   */
  getVariant(): string | null {
    return this.experimentParams.exp_variant;
  }

  /**
   * Get experiment name
   */
  getExperimentName(): string | null {
    return this.experimentParams.exp_name;
  }

  /**
   * Build custom page path with experiment parameters
   * This creates unique paths for funnel tracking in GA4
   */
  buildCustomPagePath(basePath: string): string {
    if (!this.isInExperiment()) {
      return basePath; // No experiment, return original path
    }

    const params = new URLSearchParams();

    if (this.experimentParams.exp_name) {
      params.set("exp_name", this.experimentParams.exp_name);
    }
    if (this.experimentParams.exp_variant) {
      params.set("exp_variant", this.experimentParams.exp_variant);
    }
    if (this.experimentParams.exp_id) {
      params.set("exp_id", this.experimentParams.exp_id);
    }

    return `${basePath}?${params.toString()}`;
  }

  /**
   * Track page view with experiment parameters
   * ONLY tracks if user is in an experiment
   */
  trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.isInExperiment()) {
      // Not in experiment, skip tracking
      return;
    }

    const customPagePath = this.buildCustomPagePath(pagePath);

    ReactGA.send({
      hitType: "pageview",
      page: customPagePath,
      title: pageTitle || document.title,
    });

    console.log("🧪 Experiment PageView tracked:", customPagePath);
  }

  /**
   * Save experiment parameters to sessionStorage
   */
  saveToSession(): void {
    if (this.experimentParams.exp_name) {
      sessionStorage.setItem("exp_name", this.experimentParams.exp_name);
    }
    if (this.experimentParams.exp_variant) {
      sessionStorage.setItem("exp_variant", this.experimentParams.exp_variant);
    }
    if (this.experimentParams.exp_id) {
      sessionStorage.setItem("exp_id", this.experimentParams.exp_id);
    }
  }

  /**
   * Load from session (useful for pages without URL params)
   */
  static loadFromSession(): ExperimentParameters {
    return {
      exp_name: sessionStorage.getItem("exp_name"),
      exp_variant: sessionStorage.getItem("exp_variant"),
      exp_id: sessionStorage.getItem("exp_id"),
    };
  }

  /**
   * Get experiment query string
   */
  toQueryString(): string {
    if (!this.isInExperiment()) return "";

    const params = new URLSearchParams();
    Object.entries(this.experimentParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return params.toString();
  }

  /**
   * Check if user is in specific experiment
   */
  isInExperimentNamed(experimentName: string): boolean {
    return this.experimentParams.exp_name === experimentName;
  }

  /**
   * Check if user is in specific variant
   */
  isInVariant(variantName: string): boolean {
    return this.experimentParams.exp_variant === variantName;
  }

  /**
   * Debug log
   */
  debug(): void {
    console.group("🧪 Experiment Tracker");
    console.log("In Experiment:", this.isInExperiment());
    console.log("Experiment Name:", this.experimentParams.exp_name);
    console.log("Variant:", this.experimentParams.exp_variant);
    console.log("Experiment ID:", this.experimentParams.exp_id);
    console.log("Query String:", this.toQueryString());
    console.log(
      "Custom Path:",
      this.buildCustomPagePath(window.location.pathname)
    );
    console.groupEnd();
  }
}

// Export singleton
export const experimentTracker = new ExperimentTracker();

export default ExperimentTracker;
